import { useRef, useEffect, useCallback, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { ClerkProvider, useAuth } from 'capacitor-clerk';
import { AuthView, type AuthViewMode } from 'capacitor-clerk/native';
import { Show, RedirectToSignIn } from '@clerk/react';
import { Capacitor } from '@capacitor/core';
import { setAuthTokenGetter } from './api/client';
import ReportEditor from './pages/ReportEditor';
import ClientPortal from './pages/ClientPortal';
import FixListPage from './pages/ClientPortal/FixList';
import SigningPage from './pages/SigningPage';
import BookingPage from './pages/BookingPage';
import InspectorProfilePage from './pages/InspectorProfilePage';
import Analytics from './pages/Analytics';
import Contacts from './pages/Contacts';
import Settings from './pages/Settings';
import Scheduling from './pages/Scheduling';
import InspectionList from './pages/InspectionList';
import InspectionView from './pages/InspectionView';
import ReportList from './pages/ReportList';
import TemplatesPage from './pages/TemplatesPage';
import TemplatesIndexPage from './pages/TemplatesIndexPage';
import TemplateSectionPage from './pages/TemplateSectionPage';
import DeficiencyLibraryPage from './pages/DeficiencyLibraryPage';
import OnboardingWizard from './pages/OnboardingWizard';
import PrivacyPage from './pages/PrivacyPage';
import { AppShell } from './components/shell/AppShell';
import { ToastProvider } from './ui';
import { InspectorProfileProvider, useInspectorProfile } from './contexts/InspectorProfileContext';

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!clerkPubKey) {
  throw new Error('Missing Clerk Publishable Key');
}

function App() {
  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
    >
      <ToastProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes — wrapped in a pathless layout so DismissSplash
              fires on public pages too (they don't go through the auth Show
              blocks where it's also mounted). */}
          <Route element={<><DismissSplash /><Outlet /></>}>
            {/* /r/:publicId is the canonical short URL. /portal/:publicId remains
                for backwards compatibility with emails sent before the rename.
                /r/:publicId/summary is the "Summary Mode" shareable variant —
                same component, hides full section bodies. */}
            <Route path="/r/:publicId" element={<ClientPortal />} />
            <Route path="/r/:publicId/summary" element={<ClientPortal summaryMode />} />
            <Route path="/r/:publicId/fix-list" element={<FixListPage />} />
            <Route path="/portal/:publicId" element={<ClientPortal />} />
            {/* Pre-inspection agreement public signing page. Unauthenticated:
                the client opens this from an email link to sign on their phone. */}
            <Route path="/sign/:token" element={<SigningPage />} />
            {/* External booking widget. Embedded as iframe in inspector
                websites or linked directly. Unauthenticated. */}
            <Route path="/book/:slug" element={<BookingPage />} />
            {/* Public inspector profile — shareable "business card" link.
                Distinct from /book/:slug (which IS the booking widget). */}
            <Route path="/inspector/:slug" element={<InspectorProfilePage />} />
            {/* Privacy policy — public, referenced from the App Store
                Connect listing's Privacy Policy URL field and from the
                in-app settings/footer. */}
            <Route path="/privacy" element={<PrivacyPage />} />
          </Route>
          {/* Protected Routes */}
          <Route
            path="/*"
            element={
              <>
                <Show when="signed-in">
                  <DismissSplash />
                  <AuthTokenBridge />
                  <ProtectedRoutes />
                </Show>
                {Capacitor.isNativePlatform() ? (
                  // Native auth handles its own loading + timeout fallback so
                  // first-launch can't get stuck on a blank canvas while Clerk
                  // JS is initialising (or hung). See NativeAuthGate.
                  <>
                    <DismissSplash />
                    <NativeAuthGate />
                  </>
                ) : (
                  <Show when="signed-out">
                    <DismissSplash />
                    <RedirectToSignIn />
                  </Show>
                )}
              </>
            }
          />
        </Routes>
      </BrowserRouter>
      </ToastProvider>
    </ClerkProvider>
  );
}

const SPLASH_MIN_MS = 1500;
const splashShownAt = Date.now();

function DismissSplash() {
  useEffect(() => {
    const el = document.getElementById('splash-overlay');
    if (!el) return;
    const elapsed = Date.now() - splashShownAt;
    const remaining = Math.max(0, SPLASH_MIN_MS - elapsed);
    const id = setTimeout(() => {
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 350);
    }, remaining);
    return () => clearTimeout(id);
  }, []);
  return null;
}

// Native sign-in gate. capacitor-clerk's <AuthView> presents the Clerk
// sign-in sheet via a native plugin call and otherwise renders null. That's
// a problem in two scenarios:
//   1. The user taps the X to dismiss the native sheet. There's no
//      dismissal event from the plugin, so the React tree still has
//      <AuthView /> mounted (= null) and the user is stranded on a blank
//      white screen.
//   2. presentAuth fails or never fires (Clerk JS hung, top-VC race during
//      TestFlight's beta-popup overlay, etc.) — same blank screen, same
//      no-way-out.
//
// Fix: render a visible "Welcome" landing as the always-on entry point.
// AuthView is only mounted once the user explicitly taps Create account
// or Sign in, in the matching mode ('signUp' / 'signIn'). Each tap bumps
// `attempt`, which remounts AuthView under a new `key` so its native
// present-sheet effect re-fires. X-dismissing the sheet falls back to
// the landing — no auto-present, no jarring jump straight into the
// dark-mode Clerk sheet on first launch.
function NativeAuthGate() {
  const { isLoaded, isSignedIn } = useAuth();
  const [attempt, setAttempt] = useState<{ n: number; mode: AuthViewMode } | null>(null);

  const present = useCallback((mode: AuthViewMode) => {
    setAttempt(prev => ({ n: (prev?.n ?? 0) + 1, mode }));
  }, []);

  if (isLoaded && isSignedIn) return null;

  return (
    <>
      <NativeAuthLanding
        loading={!isLoaded}
        onCreateAccount={() => present('signUp')}
        onSignIn={() => present('signIn')}
      />
      {attempt && <AuthView key={attempt.n} mode={attempt.mode} />}
    </>
  );
}

// Same brand mark used by the splash overlay in index.html — inlined as
// SVG so it always renders even if static asset paths trip up the
// WKWebView (which is what happened when this used <img src="/apple-
// touch-icon.png"> on build 3 — the box rendered empty on TestFlight).
function FlowspectBrandMark({ size = 80 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      width={size}
      height={size}
      className="rounded-2xl shadow-elevated"
      aria-hidden
    >
      <rect width="48" height="48" rx="13" fill="#2F6A44" />
      <text
        x="24"
        y="35.5"
        textAnchor="middle"
        fontFamily="'Source Serif 4','Source Serif Pro',Charter,Georgia,serif"
        fontSize="31.7"
        fontWeight="600"
        fill="#FAFAF7"
      >
        F
      </text>
    </svg>
  );
}

function NativeAuthLanding({
  loading,
  onCreateAccount,
  onSignIn,
}: {
  loading: boolean;
  onCreateAccount: () => void;
  onSignIn: () => void;
}) {
  return (
    <div className="min-h-screen bg-surface-0 flex flex-col items-center justify-center px-6 gap-8">
      <FlowspectBrandMark />
      <div className="text-center max-w-sm">
        <h1 className="text-display-sm font-display text-ink mb-2">Welcome to Flowspect</h1>
        <p className="text-body-md text-ink-subtle">
          Create an account to start inspecting, or sign in if you already have one.
        </p>
      </div>
      <div className="flex flex-col items-center gap-3 w-full max-w-xs">
        <button
          type="button"
          onClick={onCreateAccount}
          disabled={loading}
          className="w-full bg-brand text-ink-inverse border border-brand px-8 py-3 rounded-xl text-body-md font-medium shadow-card hover:brightness-95 active:brightness-90 transition disabled:opacity-60"
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden>
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Loading…
            </span>
          ) : (
            'Create account'
          )}
        </button>
        <button
          type="button"
          onClick={onSignIn}
          disabled={loading}
          className="text-body-md text-ink-subtle hover:text-ink active:text-ink transition disabled:opacity-60"
        >
          I already have an account · <span className="underline underline-offset-2">Sign in</span>
        </button>
      </div>
    </div>
  );
}

function AuthTokenBridge() {
  const { getToken } = useAuth();
  const stableGetToken = useCallback(
    () => getToken().then(t => t ?? null),
    [getToken],
  );
  useEffect(() => {
    setAuthTokenGetter(stableGetToken);
    return () => setAuthTokenGetter(null);
  }, [stableGetToken]);
  return null;
}

// Redirects users with `onboarding_completed = false` to the wizard. Mounted
// inside the app shell so the wizard route itself isn't gated. The check
// runs once per profile load — if it fires while the profile is still
// loading (null), it'll re-fire when the context populates.
function OnboardingGate() {
  const { profile } = useInspectorProfile();
  const navigate = useNavigate();
  const hasChecked = useRef(false);

  useEffect(() => {
    if (!profile || hasChecked.current) return;
    hasChecked.current = true;
    // Strict false check: if the field is missing for any reason
    // (older backend, network glitch returning a partial profile), don't
    // ambush the user with a wizard they can't escape.
    if (profile.onboardingCompleted === false) {
      navigate('/onboarding', { replace: true });
    }
  }, [profile, navigate]);

  return null;
}

function ProtectedRoutes() {
  // Flat route list with a pathless layout route that wraps the standard app
  // routes in <AppShell>. /onboarding sits as a sibling so the wizard renders
  // full-screen, without the navigation chrome.
  return (
    <InspectorProfileProvider>
      <Routes>
        <Route path="/onboarding" element={<OnboardingWizard />} />
        <Route element={<AppShellLayout />}>
          <Route path="/" element={<Navigate to="/inspections" replace />} />
          <Route path="/dashboard" element={<Navigate to="/inspections" replace />} />
          <Route path="/reports" element={<ReportList />} />
          <Route path="/reports/:reportId" element={<ReportEditor />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/scheduling" element={<Scheduling />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/inspections" element={<InspectionList />} />
          <Route path="/inspections/:id" element={<InspectionView />} />
          <Route path="/templates" element={<TemplatesIndexPage />} />
          <Route path="/templates/:id" element={<TemplatesPage />} />
          <Route path="/templates/:templateId/sections/:sectionId" element={<TemplateSectionPage />} />
          <Route path="/library" element={<DeficiencyLibraryPage />} />
          <Route path="/deficiency-library" element={<Navigate to="/library" replace />} />
        </Route>
      </Routes>
    </InspectorProfileProvider>
  );
}

function AppShellLayout() {
  return (
    <AppShell>
      <OnboardingGate />
      <Outlet />
    </AppShell>
  );
}

export default App;
